import { useEffect, useState, useCallback, useRef, useMemo } from "react"
import React from 'react'
import axios from "axios"
import { useNavigate, useLocation } from "react-router-dom"
import GeneralistCard from "../../../components/candidates/generalist_card.js"
import Candidate from "../../../components/candidates/candidate.js"
import CandidatesLoading from "../../../components/candidates/candidates_loading.js"
import RoleDropBanner from "../../../components/role-drop-banner.jsx"

// CSS for button animations
const buttonAnimationStyle = `
  @keyframes pulseGlow {
    0% { box-shadow: 0 0 4px rgba(2, 180, 145, 0.4); }
    50% { box-shadow: 0 0 15px rgba(2, 180, 145, 0.7); }
    100% { box-shadow: 0 0 4px rgba(2, 180, 145, 0.4); }
  }

  @keyframes gradientFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes shineEffect {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes subtleBounce {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }

  .animated-cta-button {
    position: relative;
    overflow: hidden;
    animation: pulseGlow 3s infinite, subtleBounce 6s ease-in-out infinite;
    background: linear-gradient(270deg, #02B491, #01a080, #029980, #02B491);
    background-size: 300% 100%;
    animation: gradientFlow 8s ease infinite;
    transition: transform 0.2s;
    transform-origin: center center;
    will-change: transform;
  }

  /* For mobile - simplify animations to improve performance */
  @media (max-width: 640px) {
    .animated-cta-button {
      animation: gradientFlow 8s ease infinite;
      box-shadow: 0 0 8px rgba(2, 180, 145, 0.5);
    }
  }

  .animated-cta-button::after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    animation: shineEffect 6s infinite;
    will-change: transform;
  }
  
  /* Spotlight effect styling */
  .animated-cta-button .spotlight {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 0% 0%, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 60%);
    opacity: 0;
    transition: opacity 0.1s;
    pointer-events: none;
    mix-blend-mode: overlay;
  }
  
  .animated-cta-button:hover .spotlight {
    opacity: 1;
  }
  
  /* Active touch state for buttons on mobile */
  .animated-cta-button:active {
    transform: scale(0.98);
    background-color: #01a080;
  }
  
  .animated-cta-button:active .spotlight {
    opacity: 1;
  }

  /* Permanent hover effect for candidate cards */
  .permanent-card-hover {
    box-shadow: 0 6px 15px -5px rgba(0, 0, 0, 0.1), 0 6px 8px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
    border-color: rgba(2, 180, 145, 0.2);
    transition: all 0.3s ease;
  }
  
  /* Active touch state for cards */
  .candidate-card-wrapper.active-touch {
    transform: scale(0.98);
    transition: transform 0.2s ease;
  }

  /* Optimize card hover effects for mobile */
  @media (max-width: 640px) {
    .permanent-card-hover {
      box-shadow: 0 4px 10px -4px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }
  }

  .permanent-card-hover::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    box-shadow: 0 0 0 2px rgba(2, 180, 145, 0.2);
    opacity: 0.5;
    animation: pulseCard 3s infinite;
  }

  @keyframes pulseCard {
    0% { box-shadow: 0 0 0 0 rgba(2, 180, 145, 0.3); }
    70% { box-shadow: 0 0 0 4px rgba(2, 180, 145, 0); }
    100% { box-shadow: 0 0 0 0 rgba(2, 180, 145, 0); }
  }

  /* Reduce animation impact on mobile for better performance */
  @media (max-width: 640px) {
    @keyframes pulseCard {
      0% { box-shadow: 0 0 0 0 rgba(2, 180, 145, 0.2); }
      70% { box-shadow: 0 0 0 2px rgba(2, 180, 145, 0); }
      100% { box-shadow: 0 0 0 0 rgba(2, 180, 145, 0); }
    }
  }
  
  /* Add styles for focus states to improve accessibility */
  button:focus-visible, a:focus-visible, input:focus-visible, textarea:focus-visible {
    outline: 2px solid #02B491;
    outline-offset: 2px;
  }
  
  /* Improve tap target sizes on mobile */
  @media (max-width: 640px) {
    button, a, [role="button"] {
      min-height: 44px;
      min-width: 44px;
    }
  }
`;

// Contact Form Component for collecting user details
const ContactForm = ({ onSubmit, onCancel, isVisible, startStep = 1, prefilledEmail = '' }) => {
  const [formData, setFormData] = useState({
    email: prefilledEmail
  });
  
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(startStep); // Step 1: Form, Step 2: Calendly
  const [isRendered, setIsRendered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Testimonial data
  const testimonials = [
    {
      name: "Oliver Blower",
      role: "Group CEO @ Voxsmart",
      avatar: "/images/coding_logo/voxsmart.png",
      quote: "The SDR we hired is easily in the Top 10 hires of my career (maybe even Top 5).",
      highlightedQuote: "easily in the Top 10 hires of my career",
      date: "17 Apr 2025"
    },
    {
      name: "Hugo Tilmouth",
      role: "CEO & Founder @ ChargedUp",
      avatar: "/images/coding_logo/chargedup.png",
      quote: "I genuinely think Jumpstart is head and shoulders above any other recruitment service we have used to date.",
      highlightedQuote: "Jumpstart is head and shoulders above any other recruitment service we have used to date",
      date: "08 May 2025"
    },
    {
      name: "Botty Dimanov",
      role: "CEO & Co-Founder @ Tenyks",
      avatar: "/images/coding_logo/tenyks_logo.jpeg",
      quote: "Our Jumpstarter is IRREPLACEABLE.",
      highlightedQuote: "Our Jumpstarter is IRREPLACEABLE",
      date: "08 May 2025"
    }
  ];
  
  // Calendly URL for embedding
  const calendlyUrl = "https://calendly.com/d/3rr-kts-ffr/30-min-with-jumpstart";
  
  // Handle animation timing and body scroll lock
  useEffect(() => {
    if (isVisible) {
      // Disable body scrolling when modal is open
      document.body.style.overflow = 'hidden';
      
      // Small delay to ensure the DOM element exists before starting the animation
      const timer = setTimeout(() => {
        setIsRendered(true);
      }, 10);
      return () => {
        clearTimeout(timer);
        // Re-enable body scrolling when modal is closed
        document.body.style.overflow = 'unset';
      };
    } else {
      setIsRendered(false);
      // Re-enable body scrolling when modal is not visible
      document.body.style.overflow = 'unset';
    }
  }, [isVisible]);

  // Update form data when prefilledEmail changes
  useEffect(() => {
    setFormData({
      email: prefilledEmail
    });
  }, [prefilledEmail]);

  // Update current step when startStep changes
  useEffect(() => {
    setCurrentStep(startStep);
  }, [startStep]);

  // Cleanup: ensure body scroll is restored when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    } else if (!formData.email.includes('@') || !formData.email.includes('.')) {
      newErrors.email = "Please enter a valid email";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Only send email field - exactly as requested
      const completeFormData = {
        email: formData.email
      };
      
      console.log("Submitting form data:", completeFormData);
      
      // Submit data to Airtable via API route
      const response = await axios.post('/api/register-interest', completeFormData);
      console.log("API response:", response.data);
      
      // Move to step 2 only if the API call is successful
      setCurrentStep(2);
      
      // Still call onSubmit to handle any additional logic
      onSubmit(completeFormData, false);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Extract more specific error message if available
      let errorMessage = 'Failed to submit form. Please try again.';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
        
        if (error.response.data && error.response.data.message) {
          errorMessage = `Error: ${error.response.data.message}`;
        } else if (error.response.data && error.response.data.error) {
          errorMessage = `Error: ${error.response.data.error}`;
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid form data. Please check your email and try again.';
        } else if (error.response.status === 429) {
          errorMessage = 'Too many requests. Please try again later.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'No response from server. Please check your internet connection.';
      }
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Close modal and complete the flow when booking is done
  const handleBookingComplete = () => {
    // Only email - exactly as requested
    const completeFormData = {
      email: formData.email
    };
    
    // Final submit with completed flag
    onSubmit(completeFormData, true);
  };

  // Handle closing with animation
  const handleClose = () => {
    setIsRendered(false);
    // Re-enable body scrolling immediately when closing
    document.body.style.overflow = 'unset';
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onCancel();
    }, 300);
  };
  
  if (!isVisible) return null;
  
  // Render a testimonial card
  const TestimonialCard = ({ testimonial }) => {
    // Helper function to safely highlight part of a quote
    const renderHighlightedQuote = () => {
      if (!testimonial.highlightedQuote || !testimonial.quote.includes(testimonial.highlightedQuote)) {
        return testimonial.quote;
      }
      
      const parts = testimonial.quote.split(testimonial.highlightedQuote);
      return (
        <>
          {parts[0]}
          <span className="bg-[#e6faf5] text-gray-800">{testimonial.highlightedQuote}</span>
          {parts[1]}
        </>
      );
    };
    
    return (
      <div className="mb-4 sm:mb-5 p-3 sm:p-4 border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center mb-2">
          <img 
            src={testimonial.avatar} 
            alt={testimonial.name} 
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover mr-2"
          />
          <div>
            <h4 className="font-medium text-gray-800 text-sm sm:text-base">{testimonial.name}</h4>
            <p className="text-xs sm:text-sm text-gray-500">{testimonial.role}</p>
          </div>
        </div>
        
        <p className="text-gray-800 text-sm sm:text-base mb-1">
          {renderHighlightedQuote()}
        </p>
        
        {testimonial.subtext && 
          <p className="text-xs sm:text-sm text-gray-600">{testimonial.subtext}</p>
        }
        
        <p className="text-xs text-gray-400 mt-1">{testimonial.date}</p>
      </div>
    );
  };
  
  return (
    <div 
      className={`fixed inset-0 bg-black z-50 transition-all duration-300 ease-in-out
        ${isRendered ? 'bg-opacity-60' : 'bg-opacity-0'}`}
    >
      <div 
        className={`flex items-center justify-center min-h-screen p-0 sm:p-4`}
        onClick={e => {
          // Only allow clicking off to close on Calendly step (step 2)
          if (currentStep === 2 && e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <div 
          className={`bg-white rounded-lg shadow-lg w-full flex flex-col overflow-hidden transition-all duration-300 ease-in-out relative
            ${currentStep === 1 
              ? 'max-w-5xl h-full sm:max-h-[80vh]' 
              : 'max-w-5xl h-full sm:min-h-[700px] sm:max-h-[80vh]'}
            ${isRendered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Close button (X) - Only show on Calendly step */}
          {currentStep === 2 && (
            <button 
              onClick={handleClose}
              className="absolute top-0 right-0 sm:top-1 sm:right-1 text-gray-400 hover:text-gray-600 transition-colors z-10 p-1"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-7 sm:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {currentStep === 1 ? (
            /* Step 1: Form with testimonials in two columns */
            <div className="flex flex-col sm:flex-row h-full">
              {/* Left column - Form */}
              <div className="w-full sm:w-1/2 flex flex-col h-full">
                {/* Form header */}
                <div className="px-6 sm:px-8 py-6 sm:py-6 flex-shrink-0 border-b border-gray-100">
                  <h2 className="text-xl sm:text-2xl font-medium text-gray-800 mb-2">Preview the top 1% of startup talent.</h2>
                  <p className="text-sm sm:text-base text-gray-600">Handpick from thousands of applicants. See why hundreds of startups hire via Jumpstart.</p>
                </div>
                
                {/* Form fields */}
                <div className="px-6 sm:px-8 py-6 sm:py-6 overflow-y-auto flex-grow">
                  <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-5 pb-14 sm:pb-0">
                    <div className="bg-gray-50 p-5 sm:p-5 rounded-lg border border-gray-200">
                      <label className="block text-gray-700 font-medium mb-3 text-base sm:text-base" htmlFor="email">
                        Your work email address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="e.g. name@company.com"
                          className={`w-full pl-10 pr-10 py-3.5 sm:py-3.5 text-base sm:text-base border ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-md focus:outline-none focus:ring-1 focus:ring-[#02B491]`}
                          value={formData.email}
                          onChange={handleChange}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          {isSubmitting && (
                            <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                        </div>
                      </div>
                      {errors.email && (
                        <p className="text-red-500 text-sm sm:text-sm mt-2 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          {errors.email}
                        </p>
                      )}
                    </div>
                    
                    <div className="mt-6 sticky bottom-0 pt-2 bg-white">
                      {submitError && (
                        <p className="text-red-500 text-sm sm:text-sm mb-2 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          {submitError}
                        </p>
                      )}
                      <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={`animated-cta-button w-full px-4 sm:px-6 py-3.5 sm:py-3.5 text-white font-medium rounded-md ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''} text-base sm:text-base`}
                        style={{ marginBottom: '2px' }}
                      >
                        <div className="spotlight"></div>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              
              {/* Right column - Testimonials */}
              <div className="w-full sm:w-1/2 px-6 sm:px-8 py-5 sm:py-5 hidden sm:block h-full overflow-auto border-l border-gray-100">
                <div className="mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-1">What our clients say</h3>
                  <p className="text-xs sm:text-sm text-gray-600">We've helped hundreds of startups find their perfect candidates.</p>
                </div>
                
                <div>
                  {testimonials.map((testimonial, index) => (
                    <TestimonialCard key={index} testimonial={testimonial} />
                  ))}
                </div>
              </div>
              
              {/* Mobile testimonials (visible below form on mobile only) */}
              <div className="w-full px-6 py-5 sm:hidden border-t border-gray-100 mt-2">
                <div className="mb-3">
                  <h3 className="text-base font-medium text-gray-800 mb-1">What our clients say</h3>
                  <p className="text-xs text-gray-600">We've helped hundreds of startups find their perfect candidates.</p>
                </div>
                
                {/* Show only the first testimonial on mobile to save space */}
                {testimonials.length > 0 && (
                  <TestimonialCard testimonial={testimonials[0]} />
                )}
              </div>
            </div>
          ) : (
            /* Step 2: Calendly Embed */
            <>
              <div className="flex flex-col sm:flex-row h-full">
                <div className="w-full sm:w-1/2 flex flex-col h-full">
                  <div className="px-6 sm:px-8 py-6 sm:py-5 border-b border-gray-100 flex-shrink-0">
                    <h2 className="text-xl sm:text-2xl font-medium text-gray-800 mb-1">Let's chat!</h2>
                    <p className="text-sm sm:text-base text-gray-600">Book a quick call to unlock full access to Jumpstart – browsing our candidates is completely free.</p>
                  </div>
                  
                  <div className="flex-1 w-full overflow-auto">
                    <iframe
                      src={`${calendlyUrl}?email=${encodeURIComponent(formData.email)}`}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title="Schedule a call"
                      className="h-full min-h-[550px] sm:min-h-[500px]" 
                      style={{ height: '100%' }}
                    ></iframe>
                  </div>
                  
                  <div className="px-6 sm:px-8 py-4 sm:py-4 border-t border-gray-100 flex justify-between sticky bottom-0 bg-white flex-shrink-0">
                    <button 
                      type="button" 
                      onClick={() => setCurrentStep(1)}
                      className="px-3 sm:px-5 py-2.5 sm:py-2.5 text-gray-600 rounded-md hover:bg-gray-50 transition-colors font-medium text-base"
                    >
                      Back
                    </button>
                    <button 
                      onClick={handleBookingComplete}
                      className="animated-cta-button px-5 sm:px-6 py-2.5 sm:py-2.5 text-white font-medium rounded-md text-base"
                      style={{ marginBottom: '2px' }}
                    >
                      <div className="spotlight"></div>
                      Done!
                    </button>
                  </div>
                </div>
                
                {/* Right column - Testimonials */}
                <div className="w-full sm:w-1/2 px-6 sm:px-8 py-5 sm:py-5 hidden sm:block h-full overflow-auto border-l border-gray-100">
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-1">What our clients say</h3>
                    <p className="text-xs sm:text-sm text-gray-600">We've helped hundreds of startups find their perfect candidates.</p>
                  </div>
                  
                  <div>
                    {testimonials.map((testimonial, index) => (
                      <TestimonialCard key={index} testimonial={testimonial} />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function JumpstartPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStream, setFilterStream] = useState([]);
  const [filterYearsOfExp, setFilterYearsOfExp] = useState([]);
  const [minSalary, setMinSalary] = useState("");
  const [maxSalary, setMaxSalary] = useState("");
  const [filterWorkingStyle, setFilterWorkingStyle] = useState([]);
  const [filterIndustry, setFilterIndustry] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState();
  const [searchQuery, setSearchQuery] = useState(""); 
  const [searchKeywords, setSearchKeywords] = useState([]);
  
  // Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageData, setNextPageData] = useState(null);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [prefetchController, setPrefetchController] = useState(null);
  const CANDIDATES_PER_PAGE = 50;
  const prefetchTimeoutRef = useRef(null);

  const [savedCandidates, setSavedCandidates] = useState([]);
  const [isScrollHeaderVisible, setIsScrollHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollThreshold = 50; // Pixels

  const streams = ["Generalist", "Sales", "Marketing"];

  // ----- Local Storage -----
  const saveFiltersToLocalStorage = () => {
    const filters = {
      filterStream,
      filterYearsOfExp,
      minSalary,
      maxSalary,
      filterWorkingStyle,
      filterIndustry,
      timestamp: Date.now(),
    };
    localStorage.setItem("preview_filters", JSON.stringify(filters));
  };

  const loadFiltersFromLocalStorage = () => {
    const filters = JSON.parse(localStorage.getItem("preview_filters"));
    if (filters) {
      const currentTime = Date.now();
      const twoHours = 2 * 60 * 60 * 1000;

      if (currentTime - filters.timestamp < twoHours) {
        setFilterStream(filters.filterStream);
        setFilterYearsOfExp(filters.filterYearsOfExp);
        setMinSalary(filters.minSalary);
        setMaxSalary(filters.maxSalary);
        setFilterWorkingStyle(filters.filterWorkingStyle);
        setFilterIndustry(filters.filterIndustry);
      } else {
        localStorage.removeItem("preview_filters");
      }
    }
  };

  useEffect(() => {
    loadFiltersFromLocalStorage();
  }, []);

  // Show email collection modal automatically when page loads
  useEffect(() => {
    if (!emailSubmitted) {
      setShowContactForm(true);
    }
  }, [emailSubmitted]);

  // LinkedIn Insight Tag - UTM tracking for LinkedIn campaigns
  useEffect(() => {
    // Set up LinkedIn partner ID
    window._linkedin_partner_id = "4351962";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id);

    // Create and inject LinkedIn tracking script
    if (!window.lintrk) {
      window.lintrk = function(a, b) {
        window.lintrk.q = window.lintrk.q || [];
        window.lintrk.q.push([a, b]);
      };
      window.lintrk.q = [];
    }

    // Only add script if it doesn't already exist
    if (!document.querySelector('script[src*="snap.licdn.com"]')) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
      
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(script, firstScript);
    }

    // Add noscript fallback image for users with JavaScript disabled
    if (!document.querySelector('img[src*="px.ads.linkedin.com"]')) {
      const noscriptImg = document.createElement('img');
      noscriptImg.height = '1';
      noscriptImg.width = '1';
      noscriptImg.style.display = 'none';
      noscriptImg.alt = '';
      noscriptImg.src = 'https://px.ads.linkedin.com/collect/?pid=4351962&fmt=gif';
      document.body.appendChild(noscriptImg);
    }

    console.log('LinkedIn Insight Tag initialized for UTM tracking');
  }, []);

  // Add global click interceptor for candidate cards
  useEffect(() => {
    const handleCardButtonClicks = (e) => {
      // Skip if clicking on the filters button
      if (e.target.closest('.filters-button')) {
        return;
      }

      // Find the closest candidate card wrapper
      const cardWrapper = e.target.closest('.candidate-card-wrapper');
      if (cardWrapper) {
        // Check if clicking on video-related elements
        const isVideoClick = e.target.closest('.video-player-container') || 
                            e.target.closest('.video-play-trigger') ||
                            e.target.closest('[class*="video"]') ||
                            e.target.closest('[data-video]') ||
                            (e.target.tagName === 'VIDEO') ||
                            e.target.closest('video');

        // Check if clicking a button or interactive element within the card
        const isButton = e.target.closest('button');
        const isSvgOrPath = e.target.tagName === 'svg' || e.target.tagName === 'path' || 
                           e.target.closest('svg');
        const isInteractive = e.target.closest('[style*="cursor: pointer"]') ||
                             e.target.closest('a');
                             
        if (isButton || isSvgOrPath || isInteractive || isVideoClick) {
          e.preventDefault();
          e.stopPropagation();
          if (emailSubmitted) {
            openCalendlyStep(e);
          } else {
            openContactForm(e);
          }
          return false;
        }
      }
    };
    
    document.addEventListener('click', handleCardButtonClicks, true);
    
    return () => {
      document.removeEventListener('click', handleCardButtonClicks, true);
    };
  }, [emailSubmitted]);

  // ----- Transform & Parse Helpers -----
  const transformRoles = (roles) => {
    const roleSet = new Set(roles);
    if (roleSet.size === 1) {
      if (roleSet.has("Full Stack Developer")) return ["Full Stack"];
      if (roleSet.has("Front End Developer")) return ["Front End"];
      if (roleSet.has("Back End Developer")) return ["Back End"];
    }
    if (roleSet.has("Front End Developer") && roleSet.has("Back End Developer")) {
      return ["Full Stack"];
    }
    if (roleSet.has("Full Stack Developer")) {
      return ["Full Stack"];
    }
    return roles;
  };

  const parseSalaryRange = (salary) => {
    const salaryRange = salary.match(/£(\d+)k?-?£?(\d+)?k?/i);
    if (salaryRange) {
      const minSalary = Number.parseInt(salaryRange[1]) * 1000;
      const maxSalary = salaryRange[2]
        ? Number.parseInt(salaryRange[2]) * 1000
        : minSalary;
      return { minSalary, maxSalary };
    }
    return { minSalary: null, maxSalary: null };
  };

  // Helper function to format salary range for display
  const formatSalaryRange = (minimum, ideal) => {
    if (!minimum && !ideal) return null;
    
    // Helper to clean and parse salary values
    const cleanSalary = (val) => {
      if (!val) return null;
      // Remove any non-numeric characters and convert to number
      const num = val.toString().replace(/[^0-9.]/g, '');
      return num ? parseInt(num, 10) : null;
    };
    
    const min = cleanSalary(minimum);
    const max = cleanSalary(ideal);
    
    if (min && max) {
      if (min === max) {
        return `£${min}k`;
      } else {
        return `£${min}k - £${max}k`;
      }
    } else if (min) {
      return `£${min}k+`;
    } else if (max) {
      return `Up to £${max}k`;
    }
    
    return null;
  };

  // Transformation functions for candidate data
  const transformCandidate = (candidate) => {
    // Safeguard if candidate or candidate.fields is missing
    if (!candidate || !candidate.fields) {
      return {
        id: candidate?.id || "unknown-id",
        name: candidate?.name || "No data",
        stream: candidate?.stream || "",
      };
    }

    // Use the full transformation
    return transformCandidateFull(candidate);
  };

  const transformCandidateFull = (candidate) => {
    if (!candidate || !candidate.fields) {
      return {
        id: candidate?.id || "unknown-id",
        name: candidate?.name || "No data",
        stream: candidate?.stream || "",
      };
    }

    const fields = candidate.fields || {};

    // Safely handle SWE roles
    let sweRoles = [];
    try {
      const rawSweRoles = fields["SWE relevant roles"] || "";
      if (rawSweRoles) {
        sweRoles = Array.isArray(rawSweRoles)
          ? rawSweRoles[0].split(",").map(role => role.trim())
          : typeof rawSweRoles === 'string'
            ? rawSweRoles.split(",").map(role => role.trim())
            : [];
        sweRoles = transformRoles(sweRoles);
      }
    } catch (error) {
      console.error("Error processing SWE roles:", error);
    }

    // Safely extract stream
    let stream = "";
    try {
      if (fields["Stream (from CRM)"]) {
        stream = Array.isArray(fields["Stream (from CRM)"])
          ? fields["Stream (from CRM)"][0]
          : fields["Stream (from CRM)"];
      }
    } catch (error) {
      console.error("Error processing stream:", error);
    }

    // Safely determine salary
    let salary = "";
    try {
      if (stream === "Generalist" || stream === "Marketing") {
        salary = fields["Salary - cleaned"] || "";
      } else if (stream === "Sales") {
        salary = fields["Base range + commission"] || "";
      } else if (stream === "Software Engineering") {
        salary = fields["SWE Salary - cleaned"] || "";
      }
    } catch (error) {
      console.error("Error determining salary:", error);
    }

    const { minSalary, maxSalary } = parseSalaryRange(salary);

    // Get image URL safely
    const imageUrl = fields.Photo && fields.Photo[0] ? fields.Photo[0].url : null;

    return {
      id: candidate.id || "",
      url_id: fields["ID"] || "",
      minimum_salary: fields["Minimum salary"] || "",
      ideal_salary: fields["Ideal salary"] || "",
      salary_minimum: fields["Minimum salary"] || "",
      salary_ideal: fields["Ideal salary"] || "",
      expectedSalaryRange: formatSalaryRange(fields["Minimum salary"], fields["Ideal salary"]),

      createdTime: candidate.createdTime || "",
      years_of_exp: fields["Years of exp (or commercial exp)"] || 0,
      cv: fields["CV RAW"] || "",
      video: fields["Video intro (new)"] || "",
      linkedin: fields["LinkedIn RAW"] || "",
      github: fields["Github RAW"] || "",
      
      name: fields["Name"] || "",
      lastName: fields["Surname"] || "",
      email: fields["Email"] || "",
      
      undergrad_degree_type: fields["Undergrad degree type"] || "",
      undergrad_degree_subject: fields["Undergrad subject"] || "",
      undergrad_university: fields["Undergrad university"] || "",
      undergrad_graduation_year: fields["Undergrad graduation year"] || "",
      undergrad_result: fields["Undergrad result"] || "",
      postgrad_degree_type: fields["Postgrad degree type"] || "",
      postgrad_degree_subject: fields["Postgrad subject"] || "",
      postgrad_university: fields["Postgrad university"] || "",
      postgrad_graduation_year: fields["Postgrad graduation year"] || "",
      postgrad_result: fields["Postgrad result"] || "",
      
      roles: fields["Generalist roles suitable for copy"] || "",
      industry_exp: fields["Industry experience new"] || [],
      stream: stream,
      search_fields: fields["Search Aggregate Fields"] || "",

      // Additional fields used by the card
      description: fields["Description"] || fields["Top achievement"] || "",
      most_impressive_swe: fields["Most impressive swe"] || "",
      right_to_work: fields["Right to work"] || "",
      recentTarget: fields["Recent target achieved"] || "",
      mostRecentCohort: fields["Most recent cohort"] || "",
      
      working_style: fields["Preferred working style copy"] || "",
      languages: fields["Languages copy"] || "",
      time_coding: fields["Time spent coding"] || "",
      coding_languages: fields["Coding languages new"] || "",
      
      // Add Jumpstart view (Sales) field
      "Jumpstart view (Sales)": fields["Jumpstart view (Sales)"] || "",
      
      work_experience_1_role: fields["(1) Work Experience Role"] || "",
      work_experience_1_company: fields["(1) Work Experience Company"] || "",
      work_experience_1_description: fields["(1) Work Experience Description"] || "",
      work_experience_1_duration: {
        years: fields["(1) Time spent in work experience YRS"] || 0,
        months: fields["(1) Time spent in work experience MOS"] || 0,
      },
      work_experience_2_role: fields["(2) Work Experience Role"] || "",
      work_experience_2_company: fields["(2) Work Experience Company"] || "",
      work_experience_2_description: fields["(2) Work Experience Description"] || "",
      work_experience_2_duration: {
        years: fields["(2) Time spent in work experience YRS"] || 0,
        months: fields["(2) Time spent in work experience MOS"] || 0,
      },

      swe_roles: sweRoles,
      sales: fields["Proven Sales Experience / High Potential for Sales"] || "",
      exp_swe: fields["Level of exp SWE"] || "",
      
      // Important: match the exact property names the card expects
      portfolio_swe: fields["SWE Portfolio"] || "",
      marketing_portfolio: fields["Marketing portfolio RAW"] || "",
      
      image: imageUrl,
    };
  };

  const fetchData = useCallback(async () => {
    try {
      const controller = new AbortController();
      
      setLoading(true);
      setError(null); // Clear any previous errors
      
      // Use the public endpoint that doesn't require authentication
      const response = await axios.get("/api/candidates/fetchPublicCandidates", {
        signal: controller.signal
      }).catch(error => {
        console.error("Error fetching candidates:", error);
        // Extract the most useful error message to display to user
        const errorMessage = error.response?.data?.error || error.message || "Failed to load candidates";
        setError(`Failed to load candidates. ${errorMessage}`);
        setLoading(false);
        return { data: { candidates: [] } };
      });

      const candidates = response.data.candidates || [];
      
      // If no candidates but no error, don't show an error message
      if (candidates.length === 0) {
        console.log("No candidates returned from API");
        setCandidates([]);
        setData([]);
        setLoading(false);
        return;
      }
      
      // Transform candidates using our full transformation function for consistency
      const transformedData = candidates.map(candidate => {
        try {
          return transformCandidateFull(candidate);
        } catch (error) {
          console.error("Error transforming candidate:", error, candidate);
          // Return a minimal valid candidate object to prevent crashes
          return {
            id: candidate.id || "unknown-id",
            name: "Error loading candidate",
            stream: "",
            industry_exp: [],
          };
        }
      });

      setData(transformedData);
      setCandidates(transformedData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(`An unexpected error occurred: ${err.message}`);
      setLoading(false);
    }
  }, []);

  // Sorting & Setting Data
  useEffect(() => {
    const sortedCandidates = [...candidates].sort((a, b) => {
      // Ensure names are not null or undefined for robust sorting
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });

    setData(sortedCandidates);
  }, [candidates]);

  // ----- Apply Filters -----
  const applyFilters = useCallback((candidatesList) => {
    // Apply filters to the candidates
    const filteredCandidates = candidatesList.filter((candidate) => {
      // Basic check for valid candidate data
      if (!candidate || !candidate.id) {
        console.warn("Invalid candidate in filter:", candidate);
        return false;
      }
      
      try {
        const matchesStream = filterStream.length
          ? filterStream.some((streamFilter) => {
              const candidateStream = candidate.stream;
              if (candidateStream === undefined || candidateStream === null) return false;
              
              // Convert to string in case it's another type
              const streamStr = String(candidateStream);
              return streamStr.toLowerCase().includes(streamFilter.toLowerCase());
            })
          : true;
    
        const matchesYearsOfExp = filterYearsOfExp.length
          ? filterYearsOfExp.some((yearsFilter) => {
              // Handle years_of_exp which might be a number or string
              const candidateYears = candidate.years_of_exp;
              if (candidateYears === undefined || candidateYears === null) return false;
              
              const yearsStr = candidateYears.toString().toLowerCase();
              return yearsStr.includes(yearsFilter.toLowerCase());
            })
          : true;
    
        // Utility function to parse salary string and convert to number
        function parseSalary(salary) {
          if (!salary) return null;
          try {
            // For strings like "£30k" or "30000"
            return Number.parseInt(String(salary).replace(/[^\d]/g, ""), 10) * (String(salary).includes("k") ? 1000 : 1);
          } catch (err) {
            console.warn(`Error parsing salary for candidate ${candidate.id}:`, err);
            return null;
          }
        }
        
        // Parse candidate salaries
        const candidateMinimumSalary = parseSalary(candidate.minimum_salary);
        const candidateIdealSalary = parseSalary(candidate.ideal_salary);
        
        // Handle "50k+" case specially
        const isMaxSalaryUnlimited = maxSalary === "50k+";
        const maxSalaryValue = isMaxSalaryUnlimited ? 50000 : (maxSalary ? maxSalary * 1000 : null);
        
        // Check if candidate salary is within range
        const matchesSalary = !maxSalaryValue || 
          (isMaxSalaryUnlimited ? (candidateMinimumSalary <= maxSalaryValue) : (candidateMinimumSalary <= maxSalaryValue));
    
        const matchesWorkingStyle = filterWorkingStyle.length
          ? filterWorkingStyle.some((styleFilter) => {
              // Handle working_style
              const workStyle = candidate.working_style;
              if (workStyle === undefined || workStyle === null) return false;
              
              // If working_style is an array (multi-select in Airtable)
              if (Array.isArray(workStyle)) {
                return workStyle.some(style => {
                  // Exact match for the Airtable value
                  return style === styleFilter;
                });
              }
              
              // If it's a string, do an exact match (not a substring)
              const workStyleStr = String(workStyle);
              return workStyleStr === styleFilter;
            })
          : true;
    
        const matchesIndustry = filterIndustry.length
          ? filterIndustry.some((industryFilter) => {
              // Handle industry_exp
              const industryExp = candidate.industry_exp;
              
              // If no industry experience, return false
              if (!industryExp || !Array.isArray(industryExp) || industryExp.length === 0) return false;
              
              // Check if any of the candidate's industries match the filter
              return industryExp.some(industry => {
                // Map UI filter options to database values
                switch(industryFilter) {
                  case "Banking":
                    return industry === "Banking" || industry === "Banking / Finance" || industry === "bankii";
                  case "Consulting":
                    return industry === "Consulting" || industry === "Consu";
                  case "Startup experience":
                    return industry === "Startup experience" || industry === "Startup";
                  case "Founder experience":
                    return industry === "Founder experience" || industry === "Founder Experience";
                  default:
                    return industry.toLowerCase().includes(industryFilter.toLowerCase());
                }
              });
            })
          : true;
    
        const matchesSearchQuery = searchKeywords.length
          ? searchKeywords.every((keyword) => {
              const searchFields = candidate.search_fields;
              if (searchFields === undefined || searchFields === null) return false;
              
              // Convert to string in case it's another type
              const searchFieldsStr = String(searchFields);
              return searchFieldsStr.toLowerCase().includes(keyword.toLowerCase());
            })
          : true;
    
        return (
          matchesStream &&
          matchesYearsOfExp &&
          matchesSalary &&
          matchesWorkingStyle &&
          matchesIndustry &&
          matchesSearchQuery
        );
      } catch (error) {
        console.error(`Error filtering candidate ${candidate.id}:`, error, candidate);
        return false; // Skip candidates that cause errors
      }
    });
    
    return filteredCandidates;
  }, [
    filterStream,
    filterYearsOfExp,
    minSalary,
    maxSalary,
    filterWorkingStyle,
    filterIndustry,
    searchKeywords,
  ]);

  const allFilteredCandidates = applyFilters(data);
  const totalPages = Math.ceil(allFilteredCandidates.length / CANDIDATES_PER_PAGE);

  const prefetchNextPage = useCallback(() => {
    const calculatedTotalPages = Math.ceil(allFilteredCandidates.length / CANDIDATES_PER_PAGE);
    
    if (currentPage < calculatedTotalPages && !isNextPageLoading && !nextPageData) {
      setIsNextPageLoading(true);
      
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current);
      }
      
      if (prefetchController) {
        prefetchController.abort();
      }
      
      const controller = new AbortController();
      setPrefetchController(controller);
      
      prefetchTimeoutRef.current = setTimeout(() => {
        const startIndex = currentPage * CANDIDATES_PER_PAGE;
        const endIndex = startIndex + CANDIDATES_PER_PAGE;
        const nextPageCandidates = allFilteredCandidates.slice(startIndex, endIndex);
        setNextPageData(nextPageCandidates);
        setIsNextPageLoading(false);
      }, 100);
    }
  }, [currentPage, isNextPageLoading, nextPageData, CANDIDATES_PER_PAGE, prefetchController, allFilteredCandidates]);

  const getPaginatedData = useCallback(() => {
    const startIndex = (currentPage - 1) * CANDIDATES_PER_PAGE;
    const endIndex = startIndex + CANDIDATES_PER_PAGE;
    return allFilteredCandidates.slice(startIndex, endIndex);
  }, [allFilteredCandidates, currentPage]);

  const filteredData = getPaginatedData();

  useEffect(() => {
    prefetchNextPage();
    return () => {
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current);
      }
      if (prefetchController) {
        prefetchController.abort();
      }
    };
  }, [prefetchNextPage, prefetchController]);

  useEffect(() => {
    setCurrentPage(1);
    setNextPageData(null);
  }, [
    filterStream,
    filterYearsOfExp,
    minSalary,
    maxSalary,
    filterWorkingStyle,
    filterIndustry,
    searchKeywords,
  ]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchData();
    
    return () => {
      abortController.abort();
    };
  }, [fetchData]);

  useEffect(() => {
    saveFiltersToLocalStorage();
  }, [
    filterStream,
    filterYearsOfExp,
    minSalary,
    maxSalary,
    filterWorkingStyle,
    filterIndustry,
  ]);

  // ----- Handle Search -----
  const handleSearch = () => {
    if (searchQuery.trim()) {
      setSearchKeywords((prevKeywords) => [
        ...prevKeywords,
        searchQuery.trim(),
      ]);
      setSearchQuery("");
    }
  };

  // ----- Clear & Update Filters -----
  const clearFilters = () => {
    setFilterStream([]);
    setFilterWorkingStyle([]);
    setFilterIndustry([]);
    setSearchKeywords([]);
    setFilterYearsOfExp([]);
    setMaxSalary("");
  };

  const onChange = ({
    filterStream: updatedFilterStream,
    filterYearsOfExp: updatedFilterYearsOfExp,
    filterWorkingStyle: updatedFilterWorkingStyle,
    filterIndustry: updatedFilterIndustry,
    searchKeywords: updatedSearchKeywords,
  }) => {
    setFilterStream(updatedFilterStream);
    setFilterYearsOfExp(updatedFilterYearsOfExp);
    setFilterWorkingStyle(updatedFilterWorkingStyle);
    setFilterIndustry(updatedFilterIndustry);
    setSearchKeywords(updatedSearchKeywords);
  };

  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const toggleCandidate = (candidate) => {
    // Redirect to signin for shortlisting
    navigate("/signin", { state: { returnUrl: "/jumpstart-preview" } });
  };

  // Function to handle contact form submission
  const handleContactFormSubmit = (formData, isComplete) => {
    // Here you could save the form data to your database if needed
    console.log("Form submitted:", formData);
    
    // Store the user's email
    setUserEmail(formData.email);
    
    // If the flow is complete (user clicked "I've Booked"), close the modal
    if (isComplete) {
      setShowContactForm(false);
    } else {
      // Email was submitted in step 1 - close modal and mark email as submitted
      setEmailSubmitted(true);
      setShowContactForm(false);
    }
  };
  
  // Function to open contact form (email collection step)
  const openContactForm = (e) => {
    if (e) e.preventDefault();
    setShowContactForm(true);
  };

  // Function to open just the Calendly step (step 2)
  const openCalendlyStep = (e) => {
    if (e) e.preventDefault();
    setShowContactForm(true);
  };

  // Function to open appropriate modal based on email submission status
  const openCalendly = (e) => {
    if (e) e.preventDefault();
    if (emailSubmitted) {
      openCalendlyStep(e);
    } else {
      openContactForm(e);
    }
  };

  // Legacy function - no longer used but kept for compatibility
  const handleCardInteraction = (e, candidate) => {
    if (e) e.preventDefault();
    if (emailSubmitted) {
      openCalendlyStep(e);
    } else {
      openContactForm(e);
    }
  };

  // Override other button functions to open appropriate modal
  const handleSearchRedirect = (e) => {
    e.preventDefault();
    if (emailSubmitted) {
      openCalendlyStep(e);
    } else {
      openContactForm(e);
    }
  };

  const handleFiltersRedirect = (e) => {
    e.preventDefault();
    if (emailSubmitted) {
      openCalendlyStep(e);
    } else {
      openContactForm(e);
    }
  };

  // Add a ref for the button
  const ctaButtonRef = useRef(null);
  
  // Function to handle spotlight effect
  const handleMouseMove = (e) => {
    const button = ctaButtonRef.current;
    if (!button) return;
    
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left; // x position within the element
    const y = e.clientY - rect.top; // y position within the element
    
    // Calculate position as percentages
    const percentX = Math.round((x / rect.width) * 100);
    const percentY = Math.round((y / rect.height) * 100);
    
    // Update the spotlight position
    const spotlight = button.querySelector('.spotlight');
    if (spotlight) {
      spotlight.style.background = `radial-gradient(circle at ${percentX}% ${percentY}%, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0) 60%)`;
    }
    
    // Apply a subtle transform effect toward the cursor
    const translateX = (percentX - 50) / 10; // Divide by 10 to reduce the effect
    const translateY = (percentY - 50) / 10;
    button.style.transform = `translate(${translateX}px, ${translateY}px) scale(1.05)`;
  };
  
  // Function to reset the transform when cursor leaves
  const handleMouseLeave = () => {
    const button = ctaButtonRef.current;
    if (!button) return;
    button.style.transform = '';
  };

  // ----- Scroll Listener for Header -----
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY.current && currentScrollY > scrollThreshold) {
        // Scrolling down, and past the threshold: hide the header
        if (isScrollHeaderVisible) {
          setIsScrollHeaderVisible(false);
        }
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up
        if (currentScrollY === 0) {
          // Only show the header if scrolled to the very top
          if (!isScrollHeaderVisible) {
            setIsScrollHeaderVisible(true);
          }
        }
      } else if (currentScrollY === 0 && !isScrollHeaderVisible) {
        // Edge case: If already at the top (e.g. page load) and header is hidden, show it.
        // This can also handle cases where the user quickly scrolls to top without much 'upward scroll' events.
        setIsScrollHeaderVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isScrollHeaderVisible, scrollThreshold]);

  // ----- Add touch event handlers for mobile ----- //
  useEffect(() => {
    // Function to handle touch start on candidate cards
    const handleTouchStart = (e) => {
      const cardWrapper = e.target.closest('.candidate-card-wrapper');
      if (cardWrapper) {
        cardWrapper.classList.add('active-touch');
      }
    };

    // Function to handle touch end on candidate cards
    const handleTouchEnd = (e) => {
      const cardWrapper = e.target.closest('.candidate-card-wrapper');
      if (cardWrapper) {
        cardWrapper.classList.remove('active-touch');
        
        // Prevent click events on elements that should trigger the contact form
        const isButton = e.target.closest('button');
        const isSvgOrPath = e.target.tagName === 'svg' || e.target.tagName === 'path' || 
                          e.target.closest('svg');
        const isInteractive = e.target.closest('[style*="cursor: pointer"]') ||
                            e.target.closest('a');
                            
        if (!isButton && !isSvgOrPath && !isInteractive) {
          // If this isn't an interactive element, open the appropriate modal
          if (emailSubmitted) {
            openCalendlyStep(e);
          } else {
            openContactForm(e);
          }
        }
      }
    };
    
    // Add touch event listeners for mobile
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [emailSubmitted]);

  // Add additional button touch handling for mobile
  useEffect(() => {
    const handleTouchMove = (e) => {
      // Only process if ctaButtonRef exists
      const button = ctaButtonRef.current;
      if (!button) return;
      
      // Get the first touch
      const touch = e.touches[0];
      if (!touch) return;
      
      const rect = button.getBoundingClientRect();
      // Check if touch is within button
      if (
        touch.clientX >= rect.left && 
        touch.clientX <= rect.right && 
        touch.clientY >= rect.top && 
        touch.clientY <= rect.bottom
      ) {
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        // Calculate position as percentages
        const percentX = Math.round((x / rect.width) * 100);
        const percentY = Math.round((y / rect.height) * 100);
        
        // Update the spotlight position
        const spotlight = button.querySelector('.spotlight');
        if (spotlight) {
          spotlight.style.background = `radial-gradient(circle at ${percentX}% ${percentY}%, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0) 60%)`;
        }
      }
    };
    
    // Add touch event listener for CTA button
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  return (
    <div className="bg-white min-h-screen">
      {/* Inject the CSS animation styles */}
      <style>{buttonAnimationStyle}</style>
      
      {/* Contact Form Modal */}
      {showContactForm && (
        <ContactForm 
          onSubmit={handleContactFormSubmit} 
          onCancel={() => setShowContactForm(false)} 
          isVisible={showContactForm}
          startStep={emailSubmitted ? 2 : 1}
          prefilledEmail={userEmail}
        />
      )}

      {selectedCandidate ? (
        <Candidate
          candidate={selectedCandidate}
          isInList={false}
          onToggleCandidate={openCalendly}  // Redirect on candidate action
          onSelectCandidate={() => setSelectedCandidate(null)} // Allow going back
        />
      ) : (
        <>
          {/* Redesigned Header Section - Mobile Optimized */}
          <header className="bg-white sticky top-0 z-40 w-full border-b shadow-sm">
            <div className="max-w-[1500px] mx-auto px-3 sm:px-5 py-3 sm:py-6">
              {/* Logo and Top Row */}
              <div className="flex justify-between items-center mb-3 sm:mb-6">
                <div className="flex-1 max-w-[65%]">
                  <div className="flex flex-col items-start">
                    <img
                      src="https://www.jumpstart-uk.com/wp-content/uploads/2022/05/jumpstart-logo-black.svg"
                      className="h-9 sm:h-10 w-auto mb-1"
                      alt="Jumpstart Logo"
                    />
                    <span className="text-xs sm:text-sm text-gray-600 leading-tight">Where startups come to hire the top 1% of talent</span>
                  </div>
                </div>
                
                {/* New wrapper for count and button */}
                <div className="flex items-center gap-x-2 sm:gap-x-4">
                  <span className="text-xs sm:text-sm text-gray-600 font-medium hidden sm:inline">
                  You're previewing <span className="text-gray-900 font-bold">{allFilteredCandidates.length}</span> out of 350 candidates
                  </span>
                  <button 
                    ref={ctaButtonRef}
                    onClick={openCalendly}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    className="animated-cta-button text-white px-3 sm:px-8 py-2 sm:py-2.5 text-sm sm:text-base rounded-md font-medium border border-[#01a080]/30 flex items-center z-10 whitespace-nowrap"
                  >
                    <div className="spotlight"></div>
                    {emailSubmitted ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a4 4 0 118 0v4M3 5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm3 4h12m-12 4h8m-8 4h4" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                    <span className="hidden xs:inline">{emailSubmitted ? 'Book a Call' : 'Access Full Platform'}</span>
                    <span className="inline xs:hidden">{emailSubmitted ? 'Book Call' : 'Access'}</span>
                  </button>
                </div> {/* End of new wrapper */}
              </div>
              
              {/* Mobile view candidate count (re-added) */}
              <div className="sm:hidden text-center mb-3">
                <span className="text-xs text-gray-500 font-medium">
                  You're previewing <span className="text-gray-900 font-bold">{allFilteredCandidates.length}</span> out of 350 candidates
                </span>
              </div>
              
              {/* Search Row and Stream Filters - Conditionally Rendered */}
              <div 
                className={`transition-all duration-300 ease-in-out ${isScrollHeaderVisible ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
              >
                {/* Search Row */}
                <div className="flex items-center gap-2 sm:gap-3 mt-2 sm:mt-4"> {/* Added mt-4 here from original search row for spacing when visible */}
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-2 sm:pl-3 pointer-events-none">
                      <svg
                        className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 20 20"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                        />
                      </svg>
                    </div>
                    <div className="flex overflow-hidden">
                      <input
                        type="text"
                        className="bg-gray-50 border border-gray-300 border-r-0 text-gray-900 text-xs sm:text-sm w-full py-2 sm:py-3 pl-8 sm:pl-10 pr-3 rounded-l-lg focus:outline-none focus:ring-1 focus:ring-[#02B491] focus:border-[#02B491]"
                        placeholder="Search through CVs and profiles"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSearchRedirect(e);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="bg-[#02B491] hover:bg-[#029e80] text-white font-medium text-xs sm:text-sm px-4 sm:px-6 py-2 sm:py-3 rounded-r-lg transition-colors duration-200"
                        onClick={handleSearchRedirect}
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Stream filter explanation and buttons */}
                <div className="mt-3 sm:mt-4 mb-1 sm:mb-2">
                  <p className="text-xs sm:text-sm text-gray-600 font-medium mb-2 sm:mb-3">Explore recent Jumpstart candidates:</p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {streams.map((stream) => {
                      const isSelected = filterStream.includes(stream);
                      
                      // Define specific colors for each stream
                      let streamColors = {
                        selected: {
                          bg: "bg-[#02B491]",
                          text: "text-white",
                          border: "border-transparent"
                        },
                        unselected: {
                          bg: "bg-white",
                          text: "text-gray-700",
                          border: "border-gray-300"
                        }
                      };
                      
                      // Override colors based on stream
                      if (stream === "Generalist") {
                        if (isSelected) {
                          streamColors.selected = {
                            bg: "bg-emerald-600",
                            text: "text-white",
                            border: "border-emerald-700"
                          };
                        } else {
                          streamColors.unselected = {
                            bg: "bg-emerald-100",
                            text: "text-emerald-800",
                            border: "border-emerald-200"
                          };
                        }
                      } else if (stream === "Sales") {
                        if (isSelected) {
                          streamColors.selected = {
                            bg: "bg-blue-600",
                            text: "text-white",
                            border: "border-blue-700"
                          };
                        } else {
                          streamColors.unselected = {
                            bg: "bg-blue-100",
                            text: "text-blue-800",
                            border: "border-blue-200"
                          };
                        }
                      } else if (stream === "Marketing") {
                        if (isSelected) {
                          streamColors.selected = {
                            bg: "bg-purple-600",
                            text: "text-white",
                            border: "border-purple-700"
                          };
                        } else {
                          streamColors.unselected = {
                            bg: "bg-purple-100",
                            text: "text-purple-800",
                            border: "border-purple-200"
                          };
                        }
                      }
                      
                      // Define hover classes based on stream
                      let hoverClass = "hover:bg-gray-50";
                      if (stream === "Generalist" && !isSelected) {
                        hoverClass = "hover:bg-emerald-200 hover:border-emerald-300";
                      } else if (stream === "Sales" && !isSelected) {
                        hoverClass = "hover:bg-blue-200 hover:border-blue-300";
                      } else if (stream === "Marketing" && !isSelected) {
                        hoverClass = "hover:bg-purple-200 hover:border-purple-300";
                      }
                      
                      // Combine colors for final button style
                      const buttonStyle = isSelected
                        ? `${streamColors.selected.bg} ${streamColors.selected.text} ${streamColors.selected.border}`
                        : `${streamColors.unselected.bg} ${streamColors.unselected.text} ${streamColors.unselected.border} ${hoverClass}`;

                      return (
                        <button
                          key={stream}
                          onClick={() => {
                            // Toggle stream filter
                            if (filterStream.includes(stream)) {
                              setFilterStream(filterStream.filter(s => s !== stream));
                            } else {
                              setFilterStream([...filterStream, stream]);
                            }
                          }}
                          className={`py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg font-medium text-xs sm:text-sm transition-colors duration-200 border ${buttonStyle} relative`}
                        >
                          {stream}
                          {isSelected && (
                            <svg
                              className={`absolute top-0 right-0 -mt-1 -mr-1 h-4 w-4 text-white ${
                                stream === "Generalist" ? "bg-emerald-600" :
                                stream === "Sales" ? "bg-blue-600" :
                                stream === "Marketing" ? "bg-purple-600" : "bg-[#02B491]"
                              } rounded-full`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="pb-12 sm:pb-20 bg-white">
            <div className="max-w-[1500px] mx-auto">
              {loading ? (
                <div className="pt-10 sm:pt-20 px-3 sm:px-5 pl-2">
                  <CandidatesLoading />
                </div>
              ) : error ? (
                <div className="pt-10 sm:pt-20 px-3 sm:px-5 text-center">
                  <div className="text-red-600">
                    <p className="text-base sm:text-lg">{error}</p>
                    <p className="mt-2 text-sm sm:text-base">Please check your internet connection or try again later.</p>
                    <button 
                      onClick={(e) => {
                        if (emailSubmitted) {
                          openCalendlyStep(e);
                        } else {
                          openContactForm(e);
                        }
                      }}
                      className="mt-4 bg-[#02B491] hover:bg-[#029e80] text-white px-4 py-2 rounded-md text-sm sm:text-base"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              ) : candidates.length === 0 ? (
                <div className="pt-10 sm:pt-20 px-3 sm:px-5 text-center">
                  <div className="text-gray-600">
                    <p className="text-base sm:text-lg">No candidates match your search criteria.</p>
                    <p className="mt-2 text-sm sm:text-base">Try adjusting your filters or search terms.</p>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mx-2 sm:mx-4">
                      {filteredData.map((candidate, index) => (
                        <div 
                          key={index} 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (emailSubmitted) {
                              openCalendlyStep(e);
                            } else {
                              openContactForm(e);
                            }
                          }}
                          className="cursor-pointer candidate-card-wrapper permanent-card-hover relative"
                        >
                          <GeneralistCard
                            candidate={candidate}
                            isInList={false}
                            onToggleCandidate={(e) => {
                              e && e.preventDefault && e.preventDefault();
                              e && e.stopPropagation && e.stopPropagation();
                              if (emailSubmitted) {
                                openCalendlyStep(e);
                              } else {
                                openContactForm(e);
                              }
                            }}
                            onSelectCandidate={(e) => {
                              e && e.preventDefault && e.preventDefault();
                              e && e.stopPropagation && e.stopPropagation();
                              if (emailSubmitted) {
                                openCalendlyStep(e);
                              } else {
                                openContactForm(e);
                              }
                            }}
                            setCurrentlyPlayingVideoId={() => {
                              // Instead of playing video, show contact form
                              if (emailSubmitted) {
                                openCalendlyStep();
                              } else {
                                openContactForm();
                              }
                            }}
                            hideIcons={false}
                            isLoggedIn={false}
                            // Custom props to override default behavior
                            roles={[]}
                            shortlistedCandidates={[]}
                          />
                        </div>
                      ))}
                    </div>
                    
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center space-x-2 sm:space-x-4 py-3 sm:py-4 mt-3 sm:mt-4">
                        <button
                          onClick={(e) => {
                            if (emailSubmitted) {
                              openCalendlyStep(e);
                            } else {
                              openContactForm(e);
                            }
                          }}
                          disabled={currentPage === 1}
                          className={`
                            px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors
                            ${currentPage === 1 
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}
                          `}
                        >
                          Previous
                        </button>
                        
                        <div className="text-xs sm:text-sm text-gray-500">
                          Page {currentPage} of {totalPages}
                        </div>

                        <button
                          onClick={(e) => {
                            if (emailSubmitted) {
                              openCalendlyStep(e);
                            } else {
                              openContactForm(e);
                            }
                          }}
                          disabled={currentPage === totalPages}
                          className={`
                            px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors
                            ${currentPage === totalPages 
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                              : 'bg-[#02B491] text-white hover:bg-[#029e80]'}
                          `}
                        >
                          Next
                        </button>
                      </div>
                    )}
                    
                    {/* Blurred candidate cards with CTA overlay */}
                    <div className="mt-8 sm:mt-12 mb-6 sm:mb-8 relative">
                      {/* Blurred cards row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mx-2 sm:mx-4 relative">
                        {/* Generate 3 blurred card skeletons */}
                        {[1, 2, 3].map((index) => (
                          <div 
                            key={`blurred-card-${index}`}
                            className="rounded-lg shadow-md overflow-hidden bg-white border border-gray-200 p-3 sm:p-4 h-[280px] sm:h-[320px]"
                          >
                            <div className="h-20 sm:h-24 bg-gray-200 rounded-lg animate-pulse mb-2 sm:mb-3"></div>
                            <div className="h-4 sm:h-5 bg-gray-200 rounded-md w-3/4 animate-pulse mb-1 sm:mb-2"></div>
                            <div className="h-3 sm:h-4 bg-gray-200 rounded-md w-1/2 animate-pulse mb-3 sm:mb-4"></div>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                              <div className="h-5 sm:h-6 bg-gray-200 rounded-full w-14 sm:w-16 animate-pulse"></div>
                              <div className="h-5 sm:h-6 bg-gray-200 rounded-full w-16 sm:w-20 animate-pulse"></div>
                            </div>
                            <div className="space-y-1.5 sm:space-y-2">
                              <div className="h-2.5 sm:h-3 bg-gray-200 rounded-md w-full animate-pulse"></div>
                              <div className="h-2.5 sm:h-3 bg-gray-200 rounded-md w-full animate-pulse"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Blur overlay + CTA button */}
                      <div className="absolute inset-0 backdrop-blur-sm flex items-center justify-center bg-white/30">
                        <button 
                          onClick={(e) => {
                            if (emailSubmitted) {
                              openCalendlyStep(e);
                            } else {
                              openContactForm(e);
                            }
                          }}
                          className="animated-cta-button text-white px-6 sm:px-10 py-2.5 sm:py-3 rounded-md text-sm sm:text-lg font-medium border border-[#01a080]/30 flex items-center z-10 shadow-lg"
                        >
                          <div className="spotlight"></div>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="hidden sm:inline">{emailSubmitted ? "Let's chat!" : 'Unlock More Candidates'}</span>
                          <span className="inline sm:hidden">{emailSubmitted ? "Let's chat!" : 'Unlock More'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

